import { Fragment, useEffect, useState } from "react";
import { Box, Button, Typography, Checkbox, FormControlLabel, Grid } from "mui";
import { CheckboxProps } from "@mui/material/Checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { Field } from "formik";
import { Formik, Form } from "formik";

import clsx from "clsx";
import AppCard from "components/AppCard/AppCard";
import styles from "modules/Home/views/ProviderFiltersView/ProviderFiltersView.module.scss";
import {
  fetchAllProviders,
  fetchAllProvidersCards,
  fetchAllProvidersCollection,
} from "store/providers/thunks";
import { useDispatch } from "react-redux";
import { useAppSelector } from "utils/hooks/storeHooks";
import {
  selectProviders,
  selectProvidersCards,
} from "store/providers/selectors";
import { cardType } from "types/Provider";
import { selectSessionCredentials } from "store/session/selectors";
import { setEmbedToken } from "store/session";
import { getFilterRolePermission } from "store/profile/thunks";
import { setTaxonomyOptionsByRoleRequestStatus } from "store/profile";
import { selectProfileFilterRole } from "store/profile/selectors";
import { Permission } from "types/FilterRole";
import { TemplateFormValues } from "types/Template";

const ProviderFiltersView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const locationProviderId = 3;
  const isLocationProvider =
    id !== undefined && Number(id) === locationProviderId;
  const providerTypeOfCards = useAppSelector(selectProvidersCards);
  const providers = useAppSelector(selectProviders);
  const { permission } = useAppSelector(selectProfileFilterRole);
  const [cardIdSelected, setCardIdSelected] = useState<number>();
  const { profile } = useAppSelector(selectSessionCredentials);
  const cardSelectedInfo = providerTypeOfCards.find(
    (card: cardType) => card.id === cardIdSelected
  );
  let defaultTaxonomyFilters: string[] = [];
  const parsedFilterRoles = permission?.map((item) => ({
    ...item,
    providerId: Number(item.providerId),
  }));
  const filterRolePermissions: Permission[] | undefined =
    parsedFilterRoles?.filter(
      ({ providerId }: any) => String(providerId) === id
    );
  let collections: string[] = findCollectionByCardId(
    cardIdSelected,
    filterRolePermissions
  );

  function findCollectionByCardId(
    cardIdSelected?: number,
    filterRolePermissions?: Permission[]
  ): string[] {
    let collections: string[] = [];

    if (filterRolePermissions) {
      filterRolePermissions.forEach((permission) => {
        permission.filters.forEach((filter) => {
          if (filter.cardId === cardIdSelected || filter.cardId === null) {
            collections.push(...filter.collection);
          }
        });
      });
    }

    return collections;
  }

  const cardsToShow = providerTypeOfCards.filter((object: any) => {
    const filter = filterRolePermissions?.find((filtro: any) => {
      return filtro.filters.some((filter: any) => filter.cardId === object.id);
    });

    return filter !== undefined;
  });
  const providerSelected = providers.find(
    (provider: cardType) => String(provider?.id) === id
  );

  if (profile.taxonomy !== null && profile.taxonomy.length > 0) {
    defaultTaxonomyFilters = profile.taxonomy;
  }

  const initialValues: TemplateFormValues = {
    providerId: Number(id),
    cardId: cardIdSelected,
    cardValue: "",
    checkboxes: providerSelected?.id === 4 ? defaultTaxonomyFilters : [],
    reportId: providerSelected?.reportId,
  };

  const handleOnClickCard = (
    cardId: number,
    setFieldValue: (field: string, value: number) => void,
    values: TemplateFormValues
  ) => {
    setCardIdSelected(cardId);
    setFieldValue("cardId", cardId);
    if (isLocationProvider) {
      handleSubmit(values);
    }
  };

  const handleGoNext = (values: TemplateFormValues) => {
    navigate("/Graphs", { state: values });
  };

  const handleGoBack = () => {
    navigate("/Providers");
  };

  const handleSubmit = (values: TemplateFormValues) => {
    handleGoNext({
      ...values,
      cardValue:
        cardSelectedInfo?.enumerationType || (isLocationProvider && "NPI-2"),
    });
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchAllProvidersCards(id));
      dispatch(fetchAllProvidersCollection(id));
      dispatch(fetchAllProviders());
      dispatch(setEmbedToken(""));
    }
  }, [dispatch, id, cardIdSelected]);

  useEffect(() => {
    if (id) {
      dispatch(
        getFilterRolePermission(
          profile.filterRoleId,
          id,
          setTaxonomyOptionsByRoleRequestStatus
        )
      );
    }
  }, [dispatch, id, profile.filterRoleId]);

  return (
    <Box className={styles.background}>
      <Box className={styles.pageContainer}>
        <Box className={styles.pageInfo}>
          <Typography className={styles.pageDescription} variant="h2">
            {`What type of ${
              providerSelected?.title?.split(" ")[1]
            } would you like to look for?`}
          </Typography>
        </Box>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ setFieldValue, values }) => (
            <Form>
              <Box
                className={clsx(
                  styles.pageContent,
                  cardsToShow.length % 2 === 0 ? "" : styles.pageContentOdd
                )}
              >
                {cardsToShow && (
                  <Grid container spacing={3}>
                    {cardsToShow.map(
                      (
                        { id, title, description, iconName }: cardType,
                        index: number
                      ) => (
                        <Grid key={index} item>
                          <AppCard
                            description={description}
                            iconName={iconName}
                            id={id}
                            isSelected={cardIdSelected === id}
                            title={title}
                            onClick={(id) =>
                              handleOnClickCard(id, setFieldValue, values)
                            }
                          />
                        </Grid>
                      )
                    )}
                  </Grid>
                )}
                {collections && (
                  <Grid
                    className={styles.checkboxContainer}
                    container
                    spacing={2}
                  >
                    {collections.length !== 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h3">
                          {`Select the ${providerSelected?.title
                            ?.split(" ")[1]
                            .toLowerCase()} you want to view`}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6} className={styles.checkboxGroupLeft}>
                      {collections
                        ?.slice(0, Math.ceil(collections.length / 2))
                        ?.map((title: string) => (
                          <div key={title}>
                            <Field
                              name="checkboxes"
                              type="checkbox"
                              value={title}
                            >
                              {({
                                field: { checked, ...restOfProps },
                              }: {
                                field: CheckboxProps;
                              }) => (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      {...restOfProps}
                                      defaultChecked={defaultTaxonomyFilters.includes(
                                        title
                                      )}
                                    />
                                  }
                                  label={title}
                                />
                              )}
                            </Field>
                          </div>
                        ))}
                    </Grid>
                    <Grid className={styles.checkboxGroupRight} item xs={6}>
                      {collections
                        ?.slice(Math.ceil(collections.length / 2))
                        ?.map((title: string) => (
                          <div key={title}>
                            <Field
                              name="checkboxes"
                              type="checkbox"
                              value={title}
                            >
                              {({
                                field: { checked, ...restOfProps },
                              }: {
                                field: CheckboxProps;
                              }) => (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      {...restOfProps}
                                      defaultChecked={defaultTaxonomyFilters.includes(
                                        title
                                      )}
                                      color="primary"
                                    />
                                  }
                                  label={title}
                                />
                              )}
                            </Field>
                          </div>
                        ))}
                    </Grid>
                  </Grid>
                )}
                <Box className={styles.buttonsContainer}>
                  <Button variant="outlinedSmall" onClick={handleGoBack}>
                    Back
                  </Button>
                  {!isLocationProvider && (
                    <Button
                      disabled={cardsToShow.length > 0 && !cardIdSelected}
                      style={{ borderRadius: "12px" }}
                      type="submit"
                      variant="containedSmall"
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default ProviderFiltersView;
